/**
 * The chase queue's Service / Client / Carer filters (Ed, 2026-09-24) —
 * pure, so they are testable without a DOM (the late.ts split).
 *
 * Every key is an id, never a display name: two carers can share a name,
 * and a nameless row (a data fault the queue shows honestly) must still be
 * filterable. Labels are for the dropdown only.
 */

export interface FilterableRow {
  service?: string | null;
  oneTouchClientId: number | null;
  clientName: string | null;
  owner: { personId: string; name: string | null } | null;
  candidates: { oneTouchCarerId: number | null; personId: string | null; name: string | null }[];
}

export interface FilterChoice {
  service: string;
  client: string;
  carer: string;
}

export interface FilterOption {
  key: string;
  label: string;
  count: number;
}

const NO_SERVICE = "__none";

export function serviceKeyOf(r: FilterableRow): string {
  return r.service || NO_SERVICE;
}

function serviceLabel(r: FilterableRow): string {
  return r.service || "No service recorded";
}

export function clientKeyOf(r: FilterableRow): string {
  return `ot:${r.oneTouchClientId ?? "?"}`;
}

export function clientLabel(r: FilterableRow): string {
  return r.clientName ?? `Client not identified (OT ${r.oneTouchClientId ?? "?"})`;
}

/** Who a row is owed by, as the queue shows it: the owner once there is
 *  one, otherwise every carer on the rota row. Filtering by a carer finds
 *  every case they could be chased for. */
export function carerKeysOf(r: FilterableRow): { key: string; label: string }[] {
  if (r.owner) return [{ key: `p:${r.owner.personId}`, label: r.owner.name ?? r.owner.personId }];
  return r.candidates.map((c) => ({
    key: c.personId ? `p:${c.personId}` : `ot:${c.oneTouchCarerId ?? "?"}`,
    label: c.name ?? `OT ${c.oneTouchCarerId ?? "?"}`,
  }));
}

export function passesFilters(r: FilterableRow, f: FilterChoice): boolean {
  if (f.service && serviceKeyOf(r) !== f.service) return false;
  if (f.client && clientKeyOf(r) !== f.client) return false;
  if (f.carer && !carerKeysOf(r).some((c) => c.key === f.carer)) return false;
  return true;
}

/** Dropdown options for one filter, from the rows in hand: each distinct
 *  value once, with how many rows it covers, sorted by label. */
export function filterOptions(
  rows: FilterableRow[],
  which: keyof FilterChoice,
): FilterOption[] {
  const by = new Map<string, FilterOption>();
  const add = (key: string, label: string) => {
    const o = by.get(key) ?? { key, label, count: 0 };
    o.count += 1;
    by.set(key, o);
  };
  for (const r of rows) {
    if (which === "service") add(serviceKeyOf(r), serviceLabel(r));
    else if (which === "client") add(clientKeyOf(r), clientLabel(r));
    else for (const c of carerKeysOf(r)) add(c.key, c.label);
  }
  return [...by.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/** Cascading options (Tali, 2026-10-14): Service → Client → Carer. Each
 *  dropdown lists only what the choices ABOVE it leave: pick a service and
 *  the client list shrinks to that service's clients; pick a client and the
 *  carer list shrinks to their carers. Choices below never narrow choices
 *  above, so a carer can always be found by first leaving service blank. */
const CASCADE: (keyof FilterChoice)[] = ["service", "client", "carer"];

export function cascadingOptions(
  rows: FilterableRow[],
  which: keyof FilterChoice,
  choice: FilterChoice,
): FilterOption[] {
  const above = CASCADE.slice(0, CASCADE.indexOf(which));
  const upstream: FilterChoice = { service: "", client: "", carer: "" };
  for (const k of above) upstream[k] = choice[k];
  return filterOptions(rows.filter((r) => passesFilters(r, upstream)), which);
}

/** After an upstream change, drop any downstream choice the new options no
 *  longer contain, so the screen never filters by something it can't show. */
export function pruneDownstream(rows: FilterableRow[], choice: FilterChoice, changed: keyof FilterChoice): FilterChoice {
  const next = { ...choice };
  for (const k of CASCADE.slice(CASCADE.indexOf(changed) + 1)) {
    if (next[k] && !cascadingOptions(rows, k, next).some((o) => o.key === next[k])) next[k] = "";
  }
  return next;
}

// ── Shift-date range (Tali/Tracey, 2026-09-28) ───────────────────────────
//
// "Which dates am I chasing?" — a different question from the lateness
// slider ("how late is it?"), so it sits beside it rather than replacing it.
// Dates are the shift's own YYYY-MM-DD (first slot), compared as strings.

export interface DatedRow {
  slots: { date: string | null }[];
}

/** "" = open-ended on that side. */
export interface DateRange {
  from: string;
  to: string;
}

export function shiftDateOf(r: DatedRow): string | null {
  const d = r.slots[0]?.date;
  return typeof d === "string" && d.length >= 10 ? d.slice(0, 10) : null;
}

/** Inclusive on both ends. A row we cannot date is always kept — never
 *  hide what we can't place. */
export function passesDateRange(r: DatedRow, range: DateRange): boolean {
  const d = shiftDateOf(r);
  if (!d) return true;
  if (range.from && d < range.from) return false;
  if (range.to && d > range.to) return false;
  return true;
}
